import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CheckCircle2, Bot, ArrowRight, FileText, Zap, Mail, Settings } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  BlogNav,
  BlogFooter,
  CTABlock,
  KeyTakeaways,
  FAQSection,
  PostMeta,
  trackBlogCTA,
  generateArticleSchema,
  generateFAQSchema
} from "./BlogComponents";

const POST_SLUG = "how-to-automate-client-screening-with-ai";
const POST_DATA = {
  title: "How to Automate Client Screening with AI",
  description: "Step-by-step guide to automating your client screening process. Includes sample questions, scoring criteria, and outcome templates.",
  author: "HeyIntake Team",
  datePublished: "2026-03-01",
  url: `/blog/${POST_SLUG}`
};

const faqs = [
  {
    question: "What is automated client screening?",
    answer: "Automated client screening uses technology (typically AI) to evaluate potential clients against your qualification criteria without manual review of every submission. It helps you identify good-fit clients faster and route poor-fit inquiries appropriately."
  },
  {
    question: "How do I define qualification criteria?",
    answer: "Start with your best past clients. What did they have in common? Budget range, timeline, specific needs, location, industry? These patterns become your criteria. Then ask intake questions that reveal whether new leads match those patterns."
  },
  {
    question: "Won't AI screening feel impersonal to clients?",
    answer: "Good screening improves the client experience. Qualified leads get faster responses. Your follow-ups are more relevant. And you can still personalize communication — AI just handles the initial triage so you can focus on building relationships with the right people."
  },
  {
    question: "How do I handle leads that don't qualify?",
    answer: "Always respond professionally. Use polite decline templates that redirect to appropriate alternatives when possible. Some leads who don't qualify today may qualify later, so maintain goodwill. Never ghost inquiries — it hurts your reputation."
  },
  {
    question: "What if the AI scores a lead incorrectly?",
    answer: "AI scoring is a directional signal, not a final verdict. You always see the score explanation, so you can override when needed. Over time, as you refine your intake questions, scoring accuracy improves. Most users find AI scores match their own assessments closely."
  }
];

export default function AutomateClientScreeningBlogPost() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <Helmet>
        <title>{POST_DATA.title} | HeyIntake Blog</title>
        <meta name="description" content={POST_DATA.description} />
        <link rel="canonical" href={`https://heyintake.com${POST_DATA.url}`} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateArticleSchema(POST_DATA))
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema(faqs))
        }} />
      </Helmet>

      <div className="min-h-screen bg-[#f5f5f0]">
        <BlogNav />

        <article className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeIn}>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                <Link to="/blog" className="hover:text-foreground">Blog</Link>
                <span>/</span>
                <span>How-To Guide</span>
              </div>

              {/* Header */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {POST_DATA.title}
              </h1>
              
              <PostMeta author={POST_DATA.author} date="March 1, 2026" readTime={12} />

              {/* Key Takeaways */}
              <KeyTakeaways items={[
                "Client screening determines fit before you invest significant time",
                "Good screening criteria come from analyzing your best past clients",
                "Ask intake questions that reveal budget, timeline, urgency, and fit",
                "AI can score and summarize submissions instantly — then you decide next steps",
                "Always respond professionally, even to leads that don't qualify"
              ]} />

              {/* Intro */}
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Client screening is the process of evaluating potential clients to determine if they're a good fit before you invest significant time. Done manually, it's tedious and inconsistent. Done with AI, it's fast, reliable, and scalable.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  This guide walks through the complete process: defining your criteria, asking the right questions, using AI to score and summarize, routing outcomes appropriately, and refining over time. We'll include sample questions and response templates you can adapt for your business.
                </p>
              </div>

              {/* Top CTA */}
              <CTABlock postSlug={POST_SLUG} location="top" variant="compact" />

              {/* Step 1 */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Step 1: Define your qualification criteria
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  Before you can automate screening, you need to know what you're screening for. Start by analyzing your best clients:
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border my-6">
                <h3 className="font-semibold text-foreground mb-4">Questions to ask yourself:</h3>
                <ul className="space-y-2">
                  {[
                    "What budget range makes a project worthwhile for us?",
                    "What timeline indicates a serious buyer vs. a tire-kicker?",
                    "What specific services or outcomes do our ideal clients need?",
                    "What industries or company sizes are the best fit?",
                    "What red flags have led to difficult projects in the past?",
                    "What signals indicate urgency or high intent?"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-muted-foreground leading-relaxed">
                Write down 5-7 criteria that define a "good fit" client. These become the foundation for your intake questions.
              </p>

              {/* Step 2 */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Step 2: Ask the right intake questions
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  Your intake form should reveal whether a lead matches your criteria. Here are sample question sets for two common scenarios:
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 my-6">
                {/* Service Business Questions */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary" />
                      Service Business (Consultant/Agency)
                    </h3>
                    <ol className="space-y-2 text-sm">
                      {[
                        "What service are you looking for?",
                        "What's the main challenge you're trying to solve?",
                        "What's your timeline for getting started?",
                        "What's your approximate budget for this project?",
                        "Have you worked with a consultant/agency before?",
                        "Who will be the decision-maker for this project?",
                        "How did you hear about us?"
                      ].map((q, i) => (
                        <li key={i} className="text-muted-foreground">{i + 1}. {q}</li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>

                {/* Law Firm Questions */}
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Law Firm (Legal Intake)
                    </h3>
                    <ol className="space-y-2 text-sm">
                      {[
                        "What type of legal matter do you need help with?",
                        "When did this situation begin?",
                        "Is there a deadline or court date we should know about?",
                        "Have you spoken with another attorney about this?",
                        "Briefly describe your situation (2-3 sentences)",
                        "What outcome are you hoping to achieve?",
                        "What's the best way to reach you?"
                      ].map((q, i) => (
                        <li key={i} className="text-muted-foreground">{i + 1}. {q}</li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              </div>

              <p className="text-muted-foreground text-sm italic">
                Tip: Include at least one open-ended question ("describe your situation"). This gives AI more context for scoring.
              </p>

              {/* Step 3 */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Step 3: Use AI to score and summarize
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  Once leads submit your intake form, AI evaluates their responses against your criteria. Here's what good AI screening provides:
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 my-6">
                {[
                  { title: "Lead Score", desc: "A numeric rating (1-10) indicating overall quality" },
                  { title: "Score Explanation", desc: "Plain-English reasoning behind the score" },
                  { title: "Key Details Summary", desc: "Highlights of important information submitted" }
                ].map((item, i) => (
                  <div key={i} className="bg-primary/5 rounded-lg p-4 text-center">
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>

              <p className="text-muted-foreground leading-relaxed">
                With AI scoring, you can review dozens of submissions in minutes. High scores get priority follow-up. Low scores get appropriate routing.
              </p>

              {/* Mid CTA */}
              <CTABlock postSlug={POST_SLUG} location="mid" />

              {/* Step 4 */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Step 4: Route outcomes appropriately
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  After scoring, leads need different responses based on their qualification level. Here are templates for common scenarios:
                </p>
              </div>

              <div className="space-y-6 my-6">
                {/* High Score Template */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    High Score (8-10): Book a call
                  </h3>
                  <div className="bg-white rounded-lg p-4 text-sm text-muted-foreground">
                    <p className="mb-2"><strong>Subject:</strong> Thanks for reaching out — let's schedule a call</p>
                    <p className="mb-2">Hi [Name],</p>
                    <p className="mb-2">Thank you for your inquiry about [service/case type]. Based on what you've shared, this sounds like something we can definitely help with.</p>
                    <p className="mb-2">I'd love to learn more about your situation. Please book a time that works for you: [Scheduling Link]</p>
                    <p>Looking forward to speaking with you.</p>
                  </div>
                </div>

                {/* Medium Score Template */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-yellow-600" />
                    Medium Score (5-7): Request more info
                  </h3>
                  <div className="bg-white rounded-lg p-4 text-sm text-muted-foreground">
                    <p className="mb-2"><strong>Subject:</strong> A few follow-up questions</p>
                    <p className="mb-2">Hi [Name],</p>
                    <p className="mb-2">Thanks for reaching out. Before we schedule a call, I have a couple of questions to make sure we're the right fit:</p>
                    <p className="mb-2">1. [Question about budget/timeline/scope]<br />2. [Question about decision-making/urgency]</p>
                    <p>Once I have these details, I can better advise on next steps.</p>
                  </div>
                </div>

                {/* Low Score Template */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-gray-500" />
                    Low Score (1-4): Polite redirect
                  </h3>
                  <div className="bg-white rounded-lg p-4 text-sm text-muted-foreground">
                    <p className="mb-2"><strong>Subject:</strong> Thanks for your inquiry</p>
                    <p className="mb-2">Hi [Name],</p>
                    <p className="mb-2">Thank you for reaching out. Based on what you've described, I don't think we're the best fit for your needs right now.</p>
                    <p className="mb-2">[Optional: You might want to try [alternative resource/referral] for situations like this.]</p>
                    <p>I appreciate you considering us, and I wish you the best with your [project/matter].</p>
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground text-sm">
                <strong>Pro tip:</strong> Never ghost leads. Even a polite decline maintains your professional reputation and leaves the door open for future opportunities.
              </p>

              {/* Step 5 */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Step 5: Track outcomes and refine
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  Automated screening improves over time. Track these metrics:
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border my-6">
                <ul className="space-y-3">
                  {[
                    { metric: "Conversion rate by score range", why: "Do high scores actually convert better?" },
                    { metric: "Response time for high-score leads", why: "Are you capitalizing on your best opportunities?" },
                    { metric: "Override rate", why: "How often do you disagree with AI scores?" },
                    { metric: "Lead sources by quality", why: "Which channels produce the best leads?" }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">{i + 1}</span>
                      <div>
                        <span className="font-medium text-foreground">{item.metric}</span>
                        <p className="text-sm text-muted-foreground">{item.why}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-muted-foreground leading-relaxed">
                If AI scores don't match actual outcomes, adjust your intake questions. Better questions = better scoring.
              </p>

              {/* Section: This is what HeyIntake does */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                This is exactly what HeyIntake is built for
              </h2>
              <div className="bg-primary/5 border-2 border-primary rounded-xl p-6 my-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-6 h-6 text-primary" />
                  <h3 className="text-xl font-bold text-foreground">HeyIntake automates this entire workflow</h3>
                </div>
                <p className="text-muted-foreground mb-4">
                  Every step we've covered — from intake form creation to AI scoring to follow-up emails — is built into HeyIntake:
                </p>
                <ul className="space-y-2">
                  {[
                    "AI Lead Scoring — Every submission scored automatically (1-10)",
                    "AI Intake Scoring — Detailed intake forms get deeper evaluation",
                    "Score Explanations — Plain-English reasoning, not just numbers",
                    "Visual Pipeline — Track leads through stages (new → contacted → booked → won)",
                    "Email Templates — Send follow-ups directly from the app",
                    "Scheduling Links — Include your Calendly/Acuity link in responses",
                    "File Collection — Add upload links for documents and attachments"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link to="/register" onClick={() => trackBlogCTA(POST_SLUG, 'trial', 'mid')}>
                    <span className="text-primary font-semibold hover:underline flex items-center gap-1">
                      Start your 7-day free trial <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </div>
              </div>

              {/* FAQ Section */}
              <FAQSection faqs={faqs} postSlug={POST_SLUG} />

              {/* Footer CTA */}
              <CTABlock postSlug={POST_SLUG} location="footer" />

              {/* Related Links */}
              <div className="mt-12 pt-8 border-t">
                <h3 className="text-lg font-semibold text-foreground mb-4">Related Resources</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/blog/best-ai-intake-software-for-law-firms-2026" className="text-primary hover:underline">
                      Best AI Intake Software for Law Firms (2026) →
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/top-lead-qualification-tools-for-service-businesses" className="text-primary hover:underline">
                      Top Lead Qualification Tools for Service Businesses →
                    </Link>
                  </li>
                  <li>
                    <Link to="/compare/heyintake-vs-intakeq-vs-jotform" className="text-primary hover:underline">
                      Compare HeyIntake vs IntakeQ vs Jotform →
                    </Link>
                  </li>
                  <li>
                    <Link to="/demo" className="text-primary hover:underline">
                      Book a Demo →
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-primary hover:underline">
                      View Pricing →
                    </Link>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </article>

        <BlogFooter />
      </div>
    </>
  );
}
